<template>
    <v-card flat :loading="loading">
        <v-card-title class="d-flex align-center flex-wrap">
            <span>Louver Scope Descriptions</span>
            <v-spacer />
            <v-btn
                color="primary"
                :loading="saving"
                :disabled="loading || !selectedModel"
                @click="save"
            >
                Save
            </v-btn>
        </v-card-title>
        <v-card-text>
            <v-tabs v-model="modelTab" background-color="transparent" show-arrows>
                <v-tab v-for="model in models" :key="model">
                    {{ model }}
                </v-tab>
            </v-tabs>

            <v-tabs v-model="langTab" class="mt-2" background-color="transparent">
                <v-tab>English</v-tab>
                <v-tab>Spanish</v-tab>
            </v-tabs>

            <div class="mt-3 mb-2 d-flex flex-wrap align-center">
                <span class="mr-2 text-caption grey--text text--darken-1">
                    Insert variable:
                </span>
                <v-chip
                    v-for="variable in variables"
                    :key="variable.token"
                    class="ma-1"
                    small
                    color="primary"
                    outlined
                    @click="insertVariable(variable.token)"
                >
                    {{ variable.token }}
                </v-chip>
            </div>

            <vue-editor
                v-if="!loading"
                :key="`${selectedModel}-${langTab}`"
                ref="editor"
                class="louver-scope-editor mt-2"
                v-model="currentHtml"
                :editor-options="editorOptions"
                @ready="onEditorReady"
            />
        </v-card-text>
    </v-card>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import Quill from 'quill'
import API from '@/services/api'
import { mapMutations } from 'vuex'
import {
    LOUVER_SCOPE_MODELS,
    LOUVER_SCOPE_VARIABLES,
    getLouverScopeSeed,
} from '@/constants/louverScopeDescriptionSeeds'

const Embed = Quill.import('blots/embed')
const Delta = Quill.import('delta')

class ScopeVarBlot extends Embed {
    static create(value) {
        const node = super.create(value)
        node.setAttribute('data-var', value)
        node.setAttribute('contenteditable', 'false')
        node.classList.add('ql-scope-var')
        node.innerText = `{{${value}}}`
        return node
    }

    static value(node) {
        return node.getAttribute('data-var')
    }
}

ScopeVarBlot.blotName = 'scopeVar'
ScopeVarBlot.tagName = 'SPAN'
ScopeVarBlot.className = 'ql-scope-var'

let scopeVarRegistered = false
function ensureScopeVarBlot() {
    if (!scopeVarRegistered) {
        Quill.register(ScopeVarBlot, true)
        scopeVarRegistered = true
    }
}

function htmlWithChips(html) {
    if (!html) return ''
    return html.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, token) => {
        return `<span class="ql-scope-var" contenteditable="false" data-var="${token}">{{${token}}}</span>`
    })
}

function htmlToPlaceholders(html) {
    if (!html) return ''
    const div = document.createElement('div')
    div.innerHTML = html
    div.querySelectorAll('.ql-scope-var, [data-var]').forEach(node => {
        const token = node.getAttribute('data-var')
        if (token) {
            const text = document.createTextNode(`{{${token}}}`)
            node.parentNode.replaceChild(text, node)
        }
    })
    return div.innerHTML
}

export default {
    name: 'LouverScopeDescriptions',
    components: { VueEditor },
    props: {
        setting: { type: Object, required: true },
    },
    data() {
        return {
            loading: false,
            saving: false,
            models: LOUVER_SCOPE_MODELS,
            variables: LOUVER_SCOPE_VARIABLES,
            modelTab: 0,
            langTab: 0,
            byModel: {},
            editorOptions: {
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['clean'],
                    ],
                    clipboard: {
                        matchers: [
                            [
                                'SPAN.ql-scope-var',
                                (node, delta) => {
                                    const token = node.getAttribute('data-var')
                                    if (!token) return delta
                                    return new Delta().insert({
                                        scopeVar: token,
                                    })
                                },
                            ],
                        ],
                    },
                },
            },
            quillInstance: null,
        }
    },
    computed: {
        selectedModel() {
            return this.models[this.modelTab] || this.models[0]
        },
        langKey() {
            return this.langTab === 1 ? 'spanish' : 'english'
        },
        currentHtml: {
            get() {
                const entry = this.byModel[this.selectedModel]
                return entry ? entry[this.langKey] : ''
            },
            set(value) {
                if (!this.byModel[this.selectedModel]) {
                    this.$set(this.byModel, this.selectedModel, {
                        english: '',
                        spanish: '',
                    })
                }
                this.$set(this.byModel[this.selectedModel], this.langKey, value)
            },
        },
    },
    watch: {
        modelTab() {
            this.ensureModelLoaded(this.selectedModel)
        },
    },
    async created() {
        ensureScopeVarBlot()
        await this.loadAll()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async loadAll() {
            try {
                this.loading = true
                const descriptions = await API.getAllLouverScopeDescriptions(
                    this.setting.id
                )
                const map = {}
                ;(descriptions || []).forEach(doc => {
                    map[doc.model || doc.id] = {
                        english: htmlWithChips(doc.english || ''),
                        spanish: htmlWithChips(doc.spanish || ''),
                    }
                })
                this.models.forEach(model => {
                    if (!map[model]) {
                        const seed = getLouverScopeSeed(model)
                        map[model] = {
                            english: htmlWithChips(seed.english),
                            spanish: htmlWithChips(seed.spanish),
                        }
                    }
                })
                this.byModel = map
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        ensureModelLoaded(model) {
            if (!this.byModel[model]) {
                const seed = getLouverScopeSeed(model)
                this.$set(this.byModel, model, {
                    english: htmlWithChips(seed.english),
                    spanish: htmlWithChips(seed.spanish),
                })
            }
        },
        onEditorReady(quill) {
            this.quillInstance = quill
        },
        insertVariable(token) {
            const editor = this.$refs.editor
            const quill = editor && (editor.quill || this.quillInstance)
            if (!quill) return
            const range = quill.getSelection(true)
            const index = range ? range.index : quill.getLength()
            quill.insertEmbed(index, 'scopeVar', token, 'user')
            quill.insertText(index + 1, ' ', 'user')
            quill.setSelection(index + 2, 0)
        },
        async save() {
            try {
                this.saving = true
                const entry = this.byModel[this.selectedModel] || {
                    english: '',
                    spanish: '',
                }
                await API.upsertLouverScopeDescription({
                    settingId: this.setting.id,
                    model: this.selectedModel,
                    english: htmlToPlaceholders(entry.english),
                    spanish: htmlToPlaceholders(entry.spanish),
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped>
.louver-scope-editor >>> .ql-container {
    min-height: 280px;
}
.louver-scope-editor >>> .ql-scope-var {
    background: #e3f2fd;
    border: 1px solid #90caf9;
    border-radius: 4px;
    padding: 0 4px;
    margin: 0 1px;
    color: #1565c0;
    font-family: monospace;
    font-size: 0.9em;
    user-select: none;
    cursor: default;
}
</style>
